import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import '../styles/style.css';
import '../styles/responsive.css';
import { initCiciEyes } from '../utils/custom';
import { supabase } from '../utils/supabaseClient';
import CreateProfile from '../pages/profiles/CreateProfile';
import UpgradeButton from '../components/UpgradeButton';
import ConnectWebsite from '../pages/websites/ConnectWebsite';

function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const [planName, setPlanName] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [error, setError] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const fetchPlanStatus = async () => {
    setLoadingPlan(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setPlanName('Free');
        return;
      }

      const { data, error: dbError } = await supabase
        .from('accounts')
        .select('plan')
        .eq('id', user.id)
        .single();

      if (dbError) throw dbError;

      const userPlan = data?.plan === 'pro' ? 'Pro' : 'Free';
      setPlanName(userPlan);
    } catch (err) {
      console.error('Error fetching plan:', err);
      setError('Failed to fetch plan');
      setPlanName('Free');
    } finally {
      setLoadingPlan(false);
    }
  };

  useEffect(() => {
    initCiciEyes();

    const menuButton = document.querySelector('.mobile-menu');
    const burger = document.querySelector('.burger');
    const menuContent = document.querySelector('.nav-links');

    if (!menuButton || !burger || !menuContent) return;

    const toggleMenu = () => {
      burger.classList.toggle('open');
      menuContent.classList.toggle('open');
    };

    const closeMenu = () => {
      burger.classList.remove('open');
      menuContent.classList.remove('open');
    };

    menuButton.addEventListener('click', toggleMenu);
    menuContent.querySelectorAll('a').forEach(link =>
      link.addEventListener('click', closeMenu)
    );

    return () => {
      menuButton.removeEventListener('click', toggleMenu);
      menuContent.querySelectorAll('a').forEach(link =>
        link.removeEventListener('click', closeMenu)
      );
    };
  }, []);

  useEffect(() => {
    const handleOpenModal = () => setShowProfileModal(true);
    window.addEventListener('open-profile-modal', handleOpenModal);

    return () => {
      window.removeEventListener('open-profile-modal', handleOpenModal);
    };
  }, []);

  useEffect(() => {
    fetchPlanStatus();
  }, []);

  return (
    <div className="app-layout">
      <nav>
        <div className="cici-container">
          <span>Veetrix</span>

          <button className="mobile-menu" aria-label="Toggle menu">
            <div className="burger">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        </div>

        <div className="nav-links">
          <div className="nav-links-container">
            <div>
              <p>Content</p>
              <ul>
                <li className={isActive('/create') ? 'active' : ''}>
                  <i className="bi bi-pencil-square" />
                  <Link to="/create">Create Content</Link>
                </li>
                <li className={isActive('/content') ? 'active' : ''}>
                  <i className="bi bi-view-list" />
                  <Link to="/content">View List</Link>
                </li>
              </ul>
            </div>

            <div>
              <p>Profiles</p>
              <ul>
                <li className={showProfileModal ? 'active' : ''}>
                  <i className="bi bi-person-add" />
                  <button
                    type="button"
                    onClick={() => setShowProfileModal(true)}
                    className="btn-unset"
                  >
                    Create Profile
                  </button>
                </li>
                <li className={isActive('/profiles') ? 'active' : ''}>
                  <i className="bi bi-view-list" />
                  <Link to="/profiles">View All</Link>
                </li>
              </ul>
            </div>

            <div>
              <p>Websites</p>
              <ul>
                <li className={showConnectModal ? 'active' : ''}>
                  <i className="bi bi-globe2" />
                  <button
                    type="button"
                    onClick={() => setShowConnectModal(true)}
                    className="btn-unset"
                  >
                    Connect Website
                  </button>
                </li>
                <li className={isActive('/websites') ? 'active' : ''}>
                  <i className="bi bi-view-list" />
                  <Link to="/websites">View All</Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="nav-links-bottom">
            {planName === 'Free' && (
            <div className="user-note">
              <h3>Current Plan: Free</h3>
              <h4>
                Upgrade for multiple content generations, up to 60,000 words a month!
              </h4>
              <UpgradeButton>Upgrade to Pro</UpgradeButton>

              {error && (
                <p className="error-text">
                  {error}
                </p>
              )}
            </div>
            )}
            <ul>
              <li className={isActive('/account') ? 'active' : ''}>
                <i className="bi bi-person-circle" />
                <Link to="/account">My Account</Link>
              </li>

              {!loadingPlan && (
                planName === 'Pro' ? (
                  <li className={isActive('/support') ? 'active' : ''}>
                    <i className="bi bi-chat-left-dots" />
                    <Link to="/contact">Get Support</Link>
                  </li>
                ) : (
                  <li className={isActive('/contact') ? 'active' : ''}>
                    <i className="bi bi-envelope" />
                    <Link to="/contact">Contact Us</Link>
                  </li>
                )
              )}

              <li>
                <i className="bi bi-box-arrow-right" />
                <button
                  type="button"
                  onClick={handleLogout}
                  className="btn-unset"
                >
                  Log Out
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <main>
        <Outlet />
        <CreateProfile
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
        />
        <ConnectWebsite
          isOpen={showConnectModal}
          onClose={() => setShowConnectModal(false)}
        />
      </main>
    </div>
  );
}

export default MainLayout;
